import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAsyncHandler } from "../../../hooks/useAsyncHandler";
import { useConfirmHandler } from "../../../hooks/useConfirmHandler";
import {
  getMissingFieldCount,
  startFieldReannotation,
  type FieldReannotationField,
  type MissingFieldCountResponse,
} from "../../../lib/corpusApi";
import { ROUTES } from "../../../route_paths";
import {
  buildMissingFieldReannotation,
  describeFieldReannotationConfirmation,
  describeMissingFieldBadge,
} from "./missing_field_scan_views";

/** Scans on demand for the documents missing one chosen field, and starts the re-annotation job for
 *  that field, going to that job's page once it exists. */
export function useMissingFieldScan() {
  const navigate = useNavigate();
  const [field, setFieldState] = useState<FieldReannotationField>("peer_review");
  const [result, setResult] = useState<MissingFieldCountResponse | null>(null);

  const scan = useAsyncHandler(async () => {
    setResult(await getMissingFieldCount(field));
  });

  const { handle: confirmLaunch, dialogProps } = useConfirmHandler({
    ...(result
      ? describeFieldReannotationConfirmation(result)
      : { title: "Re-annotate?", message: "" }),
    confirmLabel: "Start job",
  });

  /** Choosing another field drops the result, which belonged to the previous one. */
  function setField(next: FieldReannotationField) {
    setFieldState(next);
    setResult(null);
  }

  function launch() {
    if (!result) return;
    const reannotation = buildMissingFieldReannotation(result);
    confirmLaunch(async () => {
      const job = await startFieldReannotation(reannotation);
      navigate(ROUTES.internal.batchJobs.detail.path({ id: job.id }));
    });
  }

  const badge = result ? describeMissingFieldBadge(result) : null;

  return { field, setField, result, badge, scan, launch, dialogProps };
}

export type UseMissingFieldScanResult = ReturnType<typeof useMissingFieldScan>;
